/**
 * @file ContractReviewDrawer.tsx
 * @description 合約評審抽屜（PDF 三節合一）。
 *
 * 顯示模式按 review.status + 當前用戶角色分支：
 *   - draft + manager/admin：先由管理組「完成初審」（確認申請文件齊全）；
 *     初審完成後方可編輯 checklist、leadOpinion、簽名 → 儲存草稿 / 提交
 *     （未初審時組長填寫/提交區禁用並提示，後端亦以 ADMIN_REVIEW_REQUIRED 把關）
 *   - lead_submitted + admin：只讀組長段，下方出現「結論」批示子表單
 *   - 其他：純查看（只讀）
 *
 * 數據經由 TanStack Query 載入；提交後 invalidate 相關 query。
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd';
import dayjs from 'dayjs';
import { message } from '@/lib/antd-app';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, contractReviewApi, type ContractReviewDTO } from '@/lib/api-client';
import { uploadFile as uploadFileToOss } from '@/lib/file-upload';
import { useAuthStore } from '@/lib/auth-context';
import { userCan } from '@zhcb/shared';
import { REVIEW_STATUS_META, CONCLUSION_LABEL } from '@/lib/contract-review-labels';
import { SignaturePadModal } from '@/components/common/SignaturePadModal';
import { getVerificationBasisText, type ContractReviewChecklistItem } from '@zhcb/shared';
import { ChecklistTable } from './ChecklistTable';
import { ScopeSnapshotView } from './ScopeSnapshotView';
import { ManagerDecideForm } from './ManagerDecideForm';

interface Props {
  /** 抽屜開關 */
  open: boolean;
  /** 當前選中的評審單；null 時抽屜內容為空 */
  review: ContractReviewDTO | null;
  /** 關閉抽屜 */
  onClose: () => void;
}

/**
 * 把 jsonb 中可能殘留的舊結構（缺 label / 缺 no）規整為合規 checklist。
 * checklist 為 null 時返回 12 個空白項以保持表格可渲染。
 */
function normalizeChecklist(raw: ContractReviewDTO['checklist']): ContractReviewChecklistItem[] {
  if (!raw || raw.length === 0) {
    return Array.from({ length: 12 }, (_, i) => ({
      no: i + 1,
      label: '',
      answer: 'yes' as const,
    }));
  }
  return raw.map((it) => ({
    no: it.no,
    label: it.label ?? '',
    answer: it.answer,
    note: it.note,
  }));
}

/**
 * 從快照讀取【驗證依據】初值（序 58）：優先讀快照已存值，否則按 scheme 回落預設文本。
 * scopeSnapshot 為 jsonb（DTO 上是 Record），此處收窄讀取 verificationBasis / scheme。
 */
function readVerificationBasis(snapshot: ContractReviewDTO['scopeSnapshot']): string {
  const s = (snapshot ?? {}) as { verificationBasis?: string | null; scheme?: string | null };
  const stored = s.verificationBasis?.trim();
  return stored ? stored : getVerificationBasisText(s.scheme);
}

/** 上傳簽名到 OSS，返回 file id（給 ManagerDecideForm / 組長段使用） */
async function uploadSignatureToOss(file: File): Promise<number> {
  const uploaded = await uploadFileToOss(file, { scope: 'case' });
  return uploaded.id;
}

/**
 * 稽核使用語言（序59.3）解析：把儲存的 auditLanguage 字串拆成
 * 「中文 / 其他」開關 + 其他自訂文字，供 UI 二選一渲染。
 * 非「中文」的任意值視為「其他」，並回填其原文為自訂文字。
 */
function parseAuditLanguage(raw?: string | null): { isOther: boolean; other: string } {
  const v = (raw ?? '').trim();
  if (!v || v === '中文') return { isOther: false, other: '' };
  return { isOther: true, other: v };
}

export function ContractReviewDrawer({ open, review, onClose }: Props) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [checklist, setChecklist] = useState<ContractReviewChecklistItem[]>(
    normalizeChecklist(review?.checklist ?? null),
  );
  const [opinion, setOpinion] = useState(review?.leadOpinion ?? '');
  // 驗證組長決議（序59.4）：受理發TAF / 不受理；不受理時 opinion 存原因（必填）
  const [leadDecision, setLeadDecision] = useState<'accept_taf' | 'reject' | undefined>(
    review?.leadDecision ?? undefined,
  );
  // 稽核使用語言（序59.3）：中文 / 其他（其他時填自訂文字）
  const [langIsOther, setLangIsOther] = useState(parseAuditLanguage(review?.auditLanguage).isOther);
  const [langOther, setLangOther] = useState(parseAuditLanguage(review?.auditLanguage).other);
  // 【驗證依據】受控值（序 58）：TAP 草稿階段可編輯 OOOO 版本，儲存時併入 scopeSnapshot
  const [verificationBasis, setVerificationBasis] = useState(
    readVerificationBasis(review?.scopeSnapshot ?? null),
  );
  const [leadSignatureId, setLeadSignatureId] = useState<number | null>(null);
  // 序162：簽名檔實際歸屬人（管理組代組長導入時 ≠ 當前登入者），僅供稽核追溯，非本人簽名時才帶
  const [leadSignatureOwnerId, setLeadSignatureOwnerId] = useState<number | null>(null);
  // 組長簽名手寫彈窗開關 & 上傳中狀態
  const [leadSigOpen, setLeadSigOpen] = useState(false);
  const [leadSigUploading, setLeadSigUploading] = useState(false);
  // 序59：組長簽名日期（預設當日、可修改）
  const [leadSignDate, setLeadSignDate] = useState<dayjs.Dayjs>(dayjs());
  // 已通過評審補正狀態：只在管理組主動開啟時使用，提交後由後端留存不可變歷史。
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState<'lead' | 'manager'>('lead');
  const [correctionFileId, setCorrectionFileId] = useState<number | undefined>();
  const [correctionDate, setCorrectionDate] = useState<dayjs.Dayjs | null>(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionUploading, setCorrectionUploading] = useState(false);

  // 切換 review 時重置內部狀態
  useEffect(() => {
    setChecklist(normalizeChecklist(review?.checklist ?? null));
    setOpinion(review?.leadOpinion ?? '');
    setLeadDecision(review?.leadDecision ?? undefined);
    const lang = parseAuditLanguage(review?.auditLanguage);
    setLangIsOther(lang.isOther);
    setLangOther(lang.other);
    setVerificationBasis(readVerificationBasis(review?.scopeSnapshot ?? null));
    setLeadSignatureId(null);
    setLeadSignatureOwnerId(null);
    setLeadSignDate(dayjs());
    setCorrectionOpen(false);
  }, [review?.id]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['contract-review', review?.id] });
    void qc.invalidateQueries({ queryKey: ['contract-reviews', review?.caseId] });
    // case 詳情頁實際使用 ['case-detail', caseId] 作為 query key（見 dispatch/$caseId.tsx），
    // 對齊才能在 lead-submit / manager-decide / cancel 後刷新 contractReviewState。
    void qc.invalidateQueries({ queryKey: ['case-detail', review?.caseId] });
  };

  // 快照方案：TAP 才允許並持久化【驗證依據】人工修改（序 58），TOP 為固定全文不編輯
  const snapshotScheme = (review?.scopeSnapshot as { scheme?: string } | null)?.scheme;
  const isTap = snapshotScheme === 'TAP';
  /** 對外顯示客戶驗證註冊碼；存量快照缺值時才回落內部評審流水號。 */
  const displayRegistrationCode =
    ((review?.scopeSnapshot?.clientCode as string | undefined) ?? '').trim() ||
    review?.reviewCode ||
    '';

  const patchMut = useMutation({
    mutationFn: async () => {
      if (!review) return;
      return contractReviewApi.update(review.id, {
        checklist,
        leadOpinion: opinion,
        // 僅 TAP 傳 verificationBasis 併回 scopeSnapshot；TOP 固定全文不寫入
        ...(isTap ? { verificationBasis } : {}),
        version: review.version,
      });
    },
    onSuccess: () => {
      message.success('已儲存草稿');
      invalidate();
    },
    onError: (e: Error) => message.error(e.message || '儲存失敗'),
  });

  // 稽核使用語言（序59.3）：其他時取自訂文字，否則固定「中文」
  const resolvedAuditLanguage = langIsOther ? langOther.trim() : '中文';

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!review) throw new Error('no review');
      // 序59.4：驗證組長須先二選一（受理發TAF / 不受理）
      if (!leadDecision) throw new Error('請選擇驗證組長決議');
      // 序159：不受理時 leadOpinion 承載原因、必填；受理時不必填寫
      if (leadDecision === 'reject' && !opinion.trim()) throw new Error('不受理時請填寫不受理原因');
      // 序59.3：選「其他」語言時須填自訂文字
      if (langIsOther && !langOther.trim()) throw new Error('請填寫其他稽核使用語言');
      // 序59.5：簽名非必填——未簽名亦可提交（可事後補簽名檔）
      return contractReviewApi.leadSubmit(review.id, {
        checklist,
        leadOpinion: opinion,
        leadDecision,
        leadSignatureFileId: leadSignatureId ?? undefined,
        // 序162：非本人簽名（管理組代組長導入）時才帶歸屬人，供後端記錄稽核追溯
        leadSignatureOwnerId: leadSignatureOwnerId ?? undefined,
        // 序59.4：簽名日期（預設當日、可修改）
        leadSignDate: leadSignDate.format('YYYY-MM-DD'),
        auditLanguage: resolvedAuditLanguage,
        version: review.version,
      });
    },
    onSuccess: () => {
      message.success('已提交評審');
      invalidate();
      onClose();
    },
    onError: (e: Error) => message.error(e.message || '提交失敗'),
  });

  // 序59.2：導入本人員工簽名檔（staff_profiles.signature_file_id）作為組長簽名
  const importSignatureMut = useMutation({
    mutationFn: async () => apiClient.get<{ fileId: number | null }>('/api/auth/me/signature'),
    onSuccess: (res) => {
      if (res.fileId) {
        setLeadSignatureId(res.fileId);
        setLeadSignatureOwnerId(null); // 導入自己的簽名檔，非代簽
        message.success('已導入我的簽名檔');
      } else {
        message.warning('尚未設定簽名檔，請至員工檔案上傳簽名，或改用手寫簽名');
      }
    },
    onError: (e: Error) => {
      console.error('[ContractReviewDrawer] import signature failed', e);
      message.error(e.message || '導入簽名檔失敗');
    },
  });

  // 序162：管理組/系統管理員代組長導入簽名檔——列出組長人選（含員工檔案簽名檔），
  // 僅在管理組/admin 開啟抽屜時查詢，避免驗證組長本人（已有「導入我的簽名檔」）多打一次請求。
  // 序47：改按能力判斷——具「建立/編輯合約評審」能力者（管理組職能）可代組長導入簽名檔
  // 候選端點 GET /lead-signature-options 守衛為 lead_submit，故須同時具備兩者，避免只勾 create 的崗位靜默 403
  const isManagerOrAdminForSignature =
    !!user &&
    userCan(user, 'case.contract_review.create') &&
    userCan(user, 'case.contract_review.lead_submit');
  const leadSignatureOptionsQ = useQuery({
    queryKey: ['contract-review', 'lead-signature-options'],
    queryFn: () => contractReviewApi.leadSignatureOptions(),
    enabled: isManagerOrAdminForSignature && open,
    staleTime: 60_000,
  });

  /** 選定候選人後，直接把該人的員工檔案簽名檔導入為組長簽名，並記錄真正歸屬人供稽核追溯 */
  const importDelegateSignature = (candidateId: number) => {
    const candidate = leadSignatureOptionsQ.data?.items.find((i) => i.id === candidateId);
    if (!candidate) return;
    if (!candidate.signatureFileId) {
      message.warning(`${candidate.name} 尚未在員工檔案設定簽名檔，請改用手寫簽名`);
      return;
    }
    setLeadSignatureId(candidate.signatureFileId);
    setLeadSignatureOwnerId(candidate.id);
    message.success(`已導入 ${candidate.name} 的簽名檔`);
  };

  const decideMut = useMutation({
    mutationFn: async (input: Parameters<typeof contractReviewApi.managerDecide>[1]) => {
      if (!review) throw new Error('no review');
      return contractReviewApi.managerDecide(review.id, input);
    },
    onSuccess: () => {
      message.success('已批示');
      invalidate();
      onClose();
    },
    onError: (e: Error) => message.error(e.message || '批示失敗'),
  });

  const correctionMut = useMutation({
    mutationFn: async () => {
      if (!review) throw new Error('no review');
      if (!correctionReason.trim()) throw new Error('請填寫補正原因');
      if (!correctionFileId && !correctionDate) throw new Error('請補正簽名檔或簽名日期');
      return contractReviewApi.correctSignature(review.id, {
        target: correctionTarget,
        signatureFileId: correctionFileId,
        signDate: correctionDate?.format('YYYY-MM-DD'),
        reason: correctionReason.trim(),
        version: review.version,
      });
    },
    onSuccess: () => {
      message.success('簽名資料已補正，補正歷史已留存');
      setCorrectionOpen(false);
      invalidate();
    },
    onError: (e: Error) => message.error(e.message || '補正失敗'),
  });

  if (!review) {
    return <Drawer open={open} onClose={onClose} size={960} title="合約評審" />;
  }

  /**
   * 序47（2026-09-25）：填寫/簽名與批示一律按能力判斷（動態 RBAC），不再硬編角色代碼。
   * 原寫法只認 admin/manager/verification_lead 角色代碼，後台「角色權限管理」勾選能力
   * 對自定義崗位無效，導致已授權者仍無法作業。
   * - 組長填寫/簽名提交：case.contract_review.lead_submit（或管理組 create 代填）
   * - 批示（受理/不受理）：case.contract_review.approve（後端同能力守衛）
   */
  const canLeadFill =
    !!user &&
    (userCan(user, 'case.contract_review.lead_submit') ||
      userCan(user, 'case.contract_review.create'));
  const canApprove = !!user && userCan(user, 'case.contract_review.approve');
  const isDraft = review.status === 'draft';
  const isLeadSubmitted = review.status === 'lead_submitted';
  // 序107：管理組初審關卡已刪除——草稿即可由組長填寫提交，不再依賴 adminReviewedAt
  const canEditDraft = isDraft && canLeadFill;
  // 「提交評審」走 POST /:id/lead-submit（守衛 lead_submit）；只具 create 的崗位僅可儲存草稿
  const canSubmitLead = isDraft && !!user && userCan(user, 'case.contract_review.lead_submit');
  const canDecide = isLeadSubmitted && canApprove;
  const canCorrectSignature =
    review.status === 'manager_approved' &&
    review.conclusion === 'accept' &&
    !!user &&
    userCan(user, 'case.contract_review.create');
  const tableReadOnly = !canEditDraft;

  /** SignaturePadModal 完成回呼：把手寫 PNG 上傳至 OSS 並記錄 fileId。 */
  const handleLeadSignatureSubmit = async (file: File) => {
    try {
      setLeadSigUploading(true);
      const id = await uploadSignatureToOss(file);
      setLeadSignatureId(id);
      setLeadSignatureOwnerId(null); // 現場手寫簽名視為本人簽署，非代簽
      message.success('組長簽名已保存');
      setLeadSigOpen(false);
    } catch (e) {
      console.error('lead signature upload failed', e);
      message.error(e instanceof Error ? e.message : '簽名上傳失敗');
      throw e;
    } finally {
      setLeadSigUploading(false);
    }
  };

  /** 開啟補正視窗時帶入該簽署人的現有日期，避免使用者重複輸入。 */
  const openCorrection = (target: 'lead' | 'manager') => {
    const currentDate = target === 'lead' ? review.leadSignDate : review.gmSignDate;
    setCorrectionTarget(target);
    setCorrectionFileId(undefined);
    setCorrectionDate(currentDate ? dayjs(currentDate) : null);
    setCorrectionReason('');
    setCorrectionOpen(true);
  };

  /** 補正簽名檔直接上傳 OSS；選檔即上傳，但須按「確認補正」才會關聯到評審。 */
  const uploadCorrectionSignature = async (file: File) => {
    try {
      setCorrectionUploading(true);
      const fileId = await uploadSignatureToOss(file);
      setCorrectionFileId(fileId);
      message.success('簽名檔已上傳，請確認補正');
    } catch (error) {
      console.error('[ContractReviewDrawer] correction signature upload failed', error);
      message.error(error instanceof Error ? error.message : '簽名檔上傳失敗');
    } finally {
      setCorrectionUploading(false);
    }
    return false;
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={960}
      destroyOnHidden
      title={
        <Space>
          <span>{`合約評審 ${displayRegistrationCode}`}</span>
          <Tag>{`第 ${review.round} 輪`}</Tag>
          <Tag color={REVIEW_STATUS_META[review.status].color}>
            {REVIEW_STATUS_META[review.status].text}
          </Tag>
          {review.conclusion && <Tag color="purple">{CONCLUSION_LABEL[review.conclusion]}</Tag>}
        </Space>
      }
    >
      {/* 第一節：確認驗證範圍快照（序 58 按 TOP/TAP 裁剪欄位）；
          TAP 草稿階段可編輯【驗證依據】OOOO 版本，儲存時併回 scopeSnapshot */}
      <ScopeSnapshotView
        snapshot={review.scopeSnapshot}
        createdAt={review.createdAt}
        editableVerificationBasis={isTap && canEditDraft}
        verificationBasisValue={verificationBasis}
        onVerificationBasisChange={setVerificationBasis}
      />

      {/* 序107：管理組初審關卡已刪除。存量單據若曾初審，唯讀展示記錄以保留審計軌跡。 */}
      {review.adminReviewedAt && (
        <Card size="small" title="管理組初審（歷史記錄）" style={{ marginTop: 24 }}>
          <Space direction="vertical" size={4}>
            <Space wrap>
              <Tag color="success">已初審</Tag>
              <span>初審人：{review.adminReviewedByName ?? review.adminReviewedBy ?? '—'}</span>
              <span style={{ color: '#999' }}>
                初審時間：{review.adminReviewedAt.slice(0, 16).replace('T', ' ')}
              </span>
            </Space>
            {review.adminReviewNote && (
              <Typography.Text type="secondary">備註：{review.adminReviewNote}</Typography.Text>
            )}
          </Space>
        </Card>
      )}

      {/* 第二節：檢查項目（TOP 12 項 / TAP 14 項，按存量 checklist 渲染） */}
      <div style={{ marginTop: 24 }}>
        <ChecklistTable
          value={checklist}
          onChange={canEditDraft ? setChecklist : undefined}
          readOnly={tableReadOnly}
        />
      </div>

      {/* 組長意見 / 簽名（序59.3 稽核使用語言、序59.4 決議二選一） */}
      <div style={{ marginTop: 24 }}>
        <Form layout="vertical">
          {/* 序59.3：稽核使用語言 —— 中文 / 其他（其他時填自訂文字） */}
          <Form.Item label="稽核使用語言" required={canEditDraft}>
            {canEditDraft ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio.Group
                  value={langIsOther ? 'other' : 'zh'}
                  onChange={(e) => setLangIsOther(e.target.value === 'other')}
                >
                  <Radio value="zh">中文</Radio>
                  <Radio value="other">其他</Radio>
                </Radio.Group>
                {langIsOther && (
                  <Input
                    placeholder="請填寫其他稽核使用語言"
                    value={langOther}
                    maxLength={50}
                    onChange={(e) => setLangOther(e.target.value)}
                  />
                )}
              </Space>
            ) : (
              <Typography.Text>{review.auditLanguage || '中文'}</Typography.Text>
            )}
          </Form.Item>

          {/* 序59.4：驗證組長意見 —— 受理發TAF / 不受理（原因）；序159：僅不受理時必填 */}
          <Form.Item label="驗證組長意見" required={canEditDraft && leadDecision === 'reject'}>
            {canEditDraft ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio.Group
                  value={leadDecision}
                  onChange={(e) => setLeadDecision(e.target.value as 'accept_taf' | 'reject')}
                >
                  <Radio value="accept_taf">受理，發 TAF 認證標誌證書</Radio>
                  <Radio value="reject">不受理</Radio>
                </Radio.Group>
                <Input.TextArea
                  rows={3}
                  value={opinion}
                  placeholder={
                    leadDecision === 'reject'
                      ? '請填寫不受理原因（必填）'
                      : '請填寫不受理原因（受理免填）'
                  }
                  onChange={(e) => setOpinion(e.target.value)}
                  maxLength={500}
                  showCount
                  status={leadDecision === 'reject' && !opinion.trim() ? 'error' : undefined}
                />
              </Space>
            ) : (
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {review.leadDecision && (
                  <Tag color={review.leadDecision === 'accept_taf' ? 'success' : 'error'}>
                    {review.leadDecision === 'accept_taf' ? '受理，發 TAF 認證標誌證書' : '不受理'}
                  </Tag>
                )}
                <Typography.Text>{review.leadOpinion || '—'}</Typography.Text>
              </Space>
            )}
          </Form.Item>

          {canEditDraft && (
            <>
              {/* 序59：簽名非必填；預設「導入簽名檔」（員工檔案簽名，如組長余南光），可手寫替代 */}
              <Form.Item
                label="組長簽名（非必填）"
                tooltip="優先導入員工檔案上傳的簽名檔；未設定簽名檔時可手寫簽名"
              >
                <Space>
                  <Button
                    type="primary"
                    ghost
                    loading={importSignatureMut.isPending}
                    onClick={() => importSignatureMut.mutate()}
                  >
                    導入簽名檔
                  </Button>
                  <Button onClick={() => setLeadSigOpen(true)}>
                    {leadSignatureId ? '重新手寫簽名' : '手寫簽名'}
                  </Button>
                  {leadSignatureId &&
                    (leadSignatureOwnerId ? (
                      <Tag color="processing">
                        代簽：
                        {leadSignatureOptionsQ.data?.items.find(
                          (i) => i.id === leadSignatureOwnerId,
                        )?.name ?? `#${leadSignatureOwnerId}`}
                      </Tag>
                    ) : (
                      <Tag color="success">已簽名</Tag>
                    ))}
                </Space>
              </Form.Item>

              {/* 序162：管理組/系統管理員可代組長導入其員工檔案簽名檔，不需組長本人親自操作 */}
              {isManagerOrAdminForSignature && (
                <Form.Item
                  label="代組長導入簽名檔"
                  tooltip="驗證組長本人不在場時，管理組可直接導入該組長（或代理人）員工檔案中已存的簽名檔，不必逐級等候組長親自簽署"
                >
                  <Select
                    placeholder="選擇組長或代理人"
                    style={{ width: 260 }}
                    loading={leadSignatureOptionsQ.isLoading}
                    onChange={importDelegateSignature}
                    options={(leadSignatureOptionsQ.data?.items ?? []).map((i) => ({
                      label: i.signatureFileId ? i.name : `${i.name}（尚無簽名檔）`,
                      value: i.id,
                      disabled: !i.signatureFileId,
                    }))}
                    notFoundContent={
                      leadSignatureOptionsQ.isLoading
                        ? undefined
                        : '尚無驗證組長人選，請至系統設置為對應人員加上「驗證組組長」角色'
                    }
                  />
                </Form.Item>
              )}
              {/* 序59.4：簽名日期預設當日、可修改 */}
              <Form.Item label="簽名日期">
                <DatePicker
                  value={leadSignDate}
                  allowClear={false}
                  onChange={(d) => d && setLeadSignDate(d)}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </>
          )}
        </Form>
      </div>

      {/* 已批示結果（序60.2：管理組可見總經理簽名與批示） */}
      {review.managerDecidedAt && (
        <Card size="small" title="三、總經理批示意見" style={{ marginTop: 24 }}>
          <Space direction="vertical" size={4}>
            <Space wrap>
              {review.conclusion && (
                <Tag color={review.conclusion === 'accept' ? 'success' : 'error'}>
                  {CONCLUSION_LABEL[review.conclusion]}
                </Tag>
              )}
              <Tag color={review.managerSignatureFileId ? 'success' : 'default'}>
                {review.managerSignatureFileId ? '已簽名' : '未附簽名檔'}
              </Tag>
              <span style={{ color: '#999' }}>
                簽名日期：{review.gmSignDate ?? review.managerDecidedAt.slice(0, 10)}
              </span>
            </Space>
            {review.managerOpinion && (
              <Typography.Text type="secondary">批示意見：{review.managerOpinion}</Typography.Text>
            )}
            {review.rejectionReason && (
              <Typography.Text type="danger">拒絕原因：{review.rejectionReason}</Typography.Text>
            )}
          </Space>
        </Card>
      )}

      {canCorrectSignature && (
        <Card size="small" title="簽名資料補正" style={{ marginTop: 24 }}>
          <Alert
            type="info"
            showIcon
            message="僅限現場稽核開始前補正；每次修改都會保留修改前後內容、原因及操作者。"
            style={{ marginBottom: 12 }}
          />
          <Space wrap>
            <Tag color={review.leadSignatureFileId ? 'success' : 'warning'}>
              組長：{review.leadSignatureFileId ? '已附簽名' : '缺簽名檔'}
              {review.leadSignDate ? ` · ${review.leadSignDate}` : ' · 缺日期'}
            </Tag>
            <Button onClick={() => openCorrection('lead')}>補正組長簽名</Button>
            <Tag color={review.managerSignatureFileId ? 'success' : 'warning'}>
              總經理：{review.managerSignatureFileId ? '已附簽名' : '缺簽名檔'}
              {review.gmSignDate ? ` · ${review.gmSignDate}` : ' · 缺日期'}
            </Tag>
            <Button onClick={() => openCorrection('manager')}>補正總經理簽名</Button>
          </Space>
        </Card>
      )}

      {/* 第三節：總經理批示（lead_submitted 階段、admin 才顯示） */}
      {canDecide && (
        <div style={{ marginTop: 24 }}>
          <h3>三、總經理批示意見</h3>
          <ManagerDecideForm
            version={review.version}
            uploadSignature={uploadSignatureToOss}
            onSubmit={async (input) => {
              await decideMut.mutateAsync(input);
            }}
          />
        </div>
      )}

      {/* 草稿階段操作按鈕 */}
      {canEditDraft && (
        <div style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={() => patchMut.mutate()} loading={patchMut.isPending}>
              儲存草稿
            </Button>
            {canSubmitLead && (
              <Button
                type="primary"
                onClick={() => submitMut.mutate()}
                loading={submitMut.isPending}
              >
                提交評審
              </Button>
            )}
          </Space>
        </div>
      )}

      {/* 組長手寫簽名彈窗 */}
      <SignaturePadModal
        open={leadSigOpen}
        title="組長簽名"
        onCancel={() => setLeadSigOpen(false)}
        loading={leadSigUploading}
        onSubmit={handleLeadSignatureSubmit}
      />

      <Modal
        open={correctionOpen}
        title="補正合約評審簽名資料"
        okText="確認補正"
        cancelText="取消"
        confirmLoading={correctionMut.isPending}
        onOk={() => correctionMut.mutate()}
        onCancel={() => setCorrectionOpen(false)}
        destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label="補正對象" required>
            <Select
              value={correctionTarget}
              options={[
                { value: 'lead', label: '驗證組長' },
                { value: 'manager', label: '總經理' },
              ]}
              onChange={(target) => openCorrection(target)}
            />
          </Form.Item>
          <Form.Item label="重新導入簽名檔">
            <Space>
              <Upload
                accept="image/png,image/jpeg"
                maxCount={1}
                showUploadList={false}
                beforeUpload={(file) => {
                  void uploadCorrectionSignature(file);
                  return false;
                }}
              >
                <Button loading={correctionUploading}>選擇簽名圖片</Button>
              </Upload>
              {correctionFileId && <Tag color="success">新簽名檔已就緒</Tag>}
            </Space>
          </Form.Item>
          <Form.Item label="簽名日期">
            <DatePicker value={correctionDate} format="YYYY-MM-DD" onChange={setCorrectionDate} />
          </Form.Item>
          <Form.Item label="補正原因" required>
            <Input.TextArea
              value={correctionReason}
              rows={3}
              maxLength={500}
              showCount
              placeholder="例如：原評審漏附組長簽名檔，依紙本正本補登"
              onChange={(event) => setCorrectionReason(event.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
}
